import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitCrmLead } from "@/lib/leads";
import { THANK_YOU } from "@/lib/thankYou";
import { useToast } from "@/hooks/use-toast";
import { trackRegistrationCompleted } from "@/lib/openaiPixel";

export const PROPERTY_TYPES = ["Studio", "Garden", "Apartamento", "Cobertura", "Casa"];

export const NEIGHBORHOODS = [
  "Sem preferência",
  "Lagoa",
  "Ipanema",
  "Leblon",
  "Copacabana",
  "Jardim Botânico",
  "Gávea",
  "Botafogo",
  "Flamengo",
  "Laranjeiras",
  "Glória",
  "Centro",
  "Tijuca",
  "Barra da Tijuca",
  "Recreio dos Bandeirantes",
];

const BEDROOMS = ["1 quarto", "2 quartos", "3 quartos", "4 quartos ou mais"];
const AREAS = ["Até 50 m²", "Até 100 m²", "Entre 100 e 200 m²", "Acima de 200 m²"];
const FEATURES = [
  "Vista Mar",
  "Vista Lagoa",
  "Vista Livre",
  "Aceita Airbnb",
  "Portaria 24h",
  "Vaga",
  "Varanda",
  "Sol da manhã",
  "Estrutura de lazer (piscina, salão de festas, churrasqueira, academia, playground)",
  "Pet friendly",
  "Próximo ao metrô",
  "Cobertura",
  "Novo ou reformado",
  "Mobiliado (geladeira, armários nos quartos)",
  "Ar condicionado (split ou janela)",
];
const PRICES = [
  "Até R$ 600.000",
  "Até R$ 1.200.000",
  "Até R$ 2.400.000",
  "Até R$ 3.600.000",
  "Acima de R$ 3.600.000",
];
const DEADLINES = ["Imediatamente", "Em até 30 dias", "Estou somente pesquisando"];
const PURCHASE_METHODS = ["À vista", "Financiamento Bancário", "Preciso vender meu imóvel antes"];
const CONTACT_PREFS = ["E-mail", "WhatsApp", "Ligação", "Não tenho interesse em receber"];

interface Props {
  /** Source label saved with the lead (e.g. landing URL). */
  source: string;
  /** Default neighborhood filled in the lead record. */
  defaultNeighborhood?: string;
  /** Optional secondary action (used by the modal to dismiss). */
  onCancel?: () => void;
  cancelLabel?: string;
  /** Called right after a successful submit, before the redirect. */
  onSuccess?: () => void;
  /** Thank-you page used for conversion tracking. */
  thankYouPath?: string;
  /** Shows only name / whatsapp / e-mail. */
  compact?: boolean;
  /** Submit button label. */
  submitLabel?: string;
}

/** Shared lead qualification form used by the welcome modal and listing pages. */
const LeadPreferencesForm = ({ source, defaultNeighborhood, onCancel, cancelLabel = "Agora não", onSuccess, thankYouPath = THANK_YOU.geral, compact = false, submitLabel = "Quero receber sugestões" }: Props) => {

  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [features, setFeatures] = useState<string[]>([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    propertyType: "",
    bedrooms: "",
    area: "",
    neighborhood: defaultNeighborhood || "",
    street: "",
    price: "",
    deadline: "",
    purchaseMethod: "",
    contactPref: "",
  });

  const toggleFeature = (f: string) =>
    setFeatures((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();

    if (!phone && !email) {
      toast({ title: "Informe WhatsApp ou e-mail para receber as sugestões", variant: "destructive" });
      return;
    }
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast({ title: "Informe um e-mail válido", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    const notes = [
      `Origem: ${source}`,
      !name && "Nome: não informado",
      !phone && email && "WhatsApp: não informado — contato por e-mail",
      form.propertyType && `Tipo de imóvel: ${form.propertyType}`,
      form.bedrooms && `Quartos: ${form.bedrooms}`,
      form.area && `Área: ${form.area}`,
      features.length > 0 && `Características: ${features.join(", ")}`,
      form.neighborhood && `Bairro: ${form.neighborhood}`,
      form.street && `Rua/Condomínio: ${form.street}`,
      form.price && `Faixa de preço: ${form.price}`,
      form.deadline && `Prazo de compra: ${form.deadline}`,
      form.purchaseMethod && `Como pretende comprar: ${form.purchaseMethod}`,
      form.contactPref && `Prefere receber por: ${form.contactPref}`,
    ]
      .filter(Boolean)
      .join(" | ");

    const { error } = await submitCrmLead({
      name,
      phone,
      email,
      propertyName: `Formulário de boas-vindas — ${source}`,
      neighborhood: form.neighborhood || defaultNeighborhood || null,
      notes: [notes],
      temperature: form.deadline === "Imediatamente" ? "quente" : undefined,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Erro ao enviar", description: error.message, variant: "destructive" });
      return;
    }
    onSuccess?.();
    toast({
      title: "Recebemos seu contato!",
      description: "Um especialista TopClass falará com você em instantes.",
    });
    // ChatGPT Ads conversion backup (in case redirect fails or is delayed)
    trackRegistrationCompleted({ source, funnel: "lead_preferences" });
    try {
      sessionStorage.setItem(
        "tc_return_to",
        window.location.pathname + window.location.search + window.location.hash
      );
    } catch {
      /* ignore */
    }
    setTimeout(() => (window.location.href = thankYouPath), 800);
  };

  return (
    <form onSubmit={submit} className="space-y-2 sm:space-y-3 mt-2">
      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        <div>
          <Label htmlFor="wl-name">Nome</Label>
          <Input id="wl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          <div>
            <Label htmlFor="wl-phone">WhatsApp com DDD</Label>
            <Input id="wl-phone" placeholder="(21) 9xxxx-xxxx" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="wl-email">E-mail</Label>
            <Input id="wl-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
        </div>

        {!compact && (<>
        <div>
          <Label>Qual tipo de imóvel você procura?</Label>

          <Select value={form.propertyType} onValueChange={(v) => setForm({ ...form, propertyType: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Quantos quartos você procura?</Label>
          <Select value={form.bedrooms} onValueChange={(v) => setForm({ ...form, bedrooms: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {BEDROOMS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Qual área (m²) é ideal?</Label>
          <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Quais características são importantes para você?</Label>
          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            {FEATURES.map((f) => (
              <label key={f} className="flex items-center gap-2 text-xs sm:text-sm cursor-pointer">
                <Checkbox checked={features.includes(f)} onCheckedChange={() => toggleFeature(f)} />
                {f}
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label>Bairro de preferência</Label>
          <Select value={form.neighborhood} onValueChange={(v) => setForm({ ...form, neighborhood: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {NEIGHBORHOODS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="wl-street">Tem preferência por alguma rua ou condomínio?</Label>
          <Input
            id="wl-street"
            placeholder="Ex.: Av. Epitácio Pessoa, Condomínio Vista Lagoa"
            value={form.street}
            onChange={(e) => setForm({ ...form, street: e.target.value })}
          />
        </div>

        <div>
          <Label>Faixa de preço</Label>
          <Select value={form.price} onValueChange={(v) => setForm({ ...form, price: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {PRICES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Prazo que está buscando para comprar seu imóvel</Label>
          <Select value={form.deadline} onValueChange={(v) => setForm({ ...form, deadline: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {DEADLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Como pretende comprar seu imóvel?</Label>
          <Select value={form.purchaseMethod} onValueChange={(v) => setForm({ ...form, purchaseMethod: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {PURCHASE_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Como prefere receber sua lista de imóveis?</Label>
          <Select value={form.contactPref} onValueChange={(v) => setForm({ ...form, contactPref: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {CONTACT_PREFS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        </>)}
      </div>

      <div className="flex gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
            {cancelLabel}
          </Button>
        )}
        <Button type="submit" variant="gold" className="flex-1" disabled={submitting}>
          {submitting ? "Enviando..." : submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default LeadPreferencesForm;
